import { observer } from '@formily/react';
import { IAnyObject, isVacuous } from '@yimoka/shared';
import { BaseStore, getEntityStore, IEntityConfig, IStore, IStoreConfig } from '@yimoka/store';
import { omit, pick } from 'lodash-es';
import React, { useEffect, useMemo } from 'react';

import { useDeepMemo } from '../../hooks/deep-memo';
import { useEntityConfig } from '../../hooks/entity-config';
import { useInitStore } from '../../hooks/store';

import { Entity, IEntityProps } from './base';
import { EntityResponse } from './response';

export const EntityDetail = observer((props: IEntityDetailProps) => {
  const { values, store, config, scope, entity, instanceKey, dispose, ...args } = props;
  const curConfig = useEntityConfig(config ?? entity?.config);

  const curStore = useMemo(() => {
    if (store instanceof BaseStore) {
      return store;
    }
    if (entity) {
      return entity.getDetail({ ...(store ?? {}), instanceKey });
    }
    return getEntityStore(store, 'detail', curConfig);
  }, [store, entity, instanceKey, curConfig]);

  useEffect(() => {
    if (!dispose || !entity || store instanceof BaseStore) return;
    return () => {
      entity.disposeDetail(instanceKey);
    };
  }, [dispose, entity, store, instanceKey]);

  const curScope = useDeepMemo(() => ({ $entity: entity, ...scope }), [entity, scope]);

  if (isVacuous(values)) {
    return (
      <FetchDetail
        notPickValues
        {...args}
        config={curConfig}
        scope={curScope}
        store={curStore}
      />
    );
  }

  return (
    <EntityValues
      notPickValues
      {...args}
      config={curConfig}
      scope={{ ...curScope, $detailStore: null }}
      store={curStore}
      values={values}
    />
  );
});

export const FetchDetail = observer((props: IFetchDetailProps) => {
  const { config, detailStore, scope, ...args } = props;
  const curConfig = useEntityConfig(config);

  const curDetailConfig = useDeepMemo(() => {
    const curDetail = detailStore ?? {};
    if (typeof curDetail.options === 'undefined') {
      curDetail.options = {};
    }
    if (typeof curDetail.options.runNow === 'undefined') {
      curDetail.options.runNow = 'always';
    }
    if (typeof curDetail.options.bindRoute === 'undefined') {
      curDetail.options.bindRoute = true;
    }
    return getEntityStore(curDetail, 'detail', curConfig);
  }, [detailStore, curConfig]);

  const curDetailStore = useInitStore(curDetailConfig);

  return (
    <Entity store={curDetailStore} >
      <EntityResponse store={curDetailStore}>
        <EntityValues
          {...args}
          config={curConfig}
          scope={{ ...scope, $detailStore: curDetailStore }}
          values={curDetailStore?.response?.data ?? {}}
        />
      </EntityResponse>
    </Entity>
  );
});

export const EntityValues = observer((props: IEntityValuesProps) => {
  const { config, values, notPickValues, scope, store = {}, omitKeys, ...args } = props;
  const curConfig = useEntityConfig(config);
  const valuesStore = useMemo(() => {
    const defaultValues = store instanceof BaseStore ? store.defaultValues : (store.defaultValues ?? {});
    const keys = Object.keys(defaultValues);
    const curValues = omitKeys?.length ? omit(values, omitKeys) : values;
    const nextValues = notPickValues || keys.length === 0
      ? curValues
      : pick(curValues, keys);

    if (store instanceof BaseStore) {
      store.setDefaultValues(nextValues);
      store.setValues(nextValues);
      return store;
    }

    // 详情接口返回后要在 Entity 首次渲染前注入默认值，否则编辑/详情 schema 会先按空值初始化。
    return {
      ...store,
      defaultValues: nextValues,
    };
  }, [notPickValues, omitKeys, store, values]);
  const curStore = useInitStore(valuesStore);
  const useScope = useDeepMemo(() => ({ $config: curConfig, ...scope }), [curConfig, scope]);

  return (
    <Entity {...args} scope={useScope} store={curStore} />
  );
});

export type IEntityDetailProps<V extends object = IAnyObject, R extends object = IAnyObject> = Partial<IEntityValuesProps<V, R>> & {
  detailStore?: IStore<V, R> | IStoreConfig<V, R>;
}

type IFetchDetailProps<V extends object = IAnyObject, R extends object = IAnyObject> = Omit<IEntityValuesProps<V, R>, 'values'> & {
  detailStore?: IStore<V, R> | IStoreConfig<V, R>;
}

export type IEntityValuesProps<V extends object = IAnyObject, R extends object = IAnyObject> = Partial<IEntityProps<V, R>> & {
  /** false 表示不继承 scope 中的 $config，与 useEntityConfig 对齐 */
  config?: IEntityConfig<V> | false;
  values: V,
  // 不对传入的 values 根据 store 的默认值进行 pick
  notPickValues?: boolean
  // 不取传入 values 中这些 key 的值
  omitKeys?: string[]
}
